import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle,
  Crown,
  Eye,
  FileText,
  HelpCircle,
  Info,
  Loader2,
  X,
} from "lucide-react";
import { openAttachmentPreview } from "../../context/AppContext";
import { formatAttachmentSize } from "./gapRecommendationUtils";

// Tooltip — reusable hover/focus tooltip (portals to body to avoid overflow clipping)
export function Tooltip({
  content,
  children,
  position = "top",
  maxWidth = 280,
  className = "",
  disabled = false,
}) {
  const id = useId();
  const triggerRef = useRef(null);
  const tipRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [resolvedPosition, setResolvedPosition] = useState(position);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const tipRect = tipRef.current?.getBoundingClientRect();
    const gap = 8;
    const edgePad = 12;
    const height = tipRect?.height || 80;
    const width = tipRect?.width || maxWidth;
    const halfWidth = width / 2;

    let pos = position;
    const spaceAbove = rect.top - edgePad;
    const spaceBelow = window.innerHeight - rect.bottom - edgePad;
    if (pos === "top" && height + gap > spaceAbove && spaceBelow >= spaceAbove) {
      pos = "bottom";
    } else if (pos === "bottom" && height + gap > spaceBelow && spaceAbove > spaceBelow) {
      pos = "top";
    }

    let top;
    let left;
    if (pos === "bottom") {
      top = rect.bottom + gap;
      left = rect.left + rect.width / 2;
    } else if (pos === "left") {
      top = rect.top + rect.height / 2;
      left = rect.left - gap;
    } else if (pos === "right") {
      top = rect.top + rect.height / 2;
      left = rect.right + gap;
    } else {
      top = rect.top - gap;
      left = rect.left + rect.width / 2;
    }

    if (pos === "top" || pos === "bottom") {
      left = Math.max(
        edgePad + halfWidth,
        Math.min(left, window.innerWidth - edgePad - halfWidth),
      );
    }

    if (pos === "top" && top - height < edgePad) {
      top = edgePad + height;
    } else if (pos === "bottom" && top + height > window.innerHeight - edgePad) {
      top = Math.max(edgePad, window.innerHeight - edgePad - height);
    }

    setResolvedPosition((prev) => (prev === pos ? prev : pos));
    setCoords((prev) => (prev.top === top && prev.left === left ? prev : { top, left }));
  }, [position, maxWidth]);

  useLayoutEffect(() => {
    if (!visible) return undefined;
    updatePosition();
    return undefined;
  }, [visible, content, updatePosition]);

  const show = useCallback(() => {
    if (disabled || !content) return;
    updatePosition();
    setVisible(true);
  }, [content, disabled, updatePosition]);

  const hide = useCallback(() => setVisible(false), []);

  useEffect(() => {
    if (!visible) return undefined;
    const onScroll = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [visible, updatePosition]);

  if (!content) return children ?? null;

  const transform =
    resolvedPosition === "bottom"
      ? "translate(-50%, 0)"
      : resolvedPosition === "left"
        ? "translate(-100%, -50%)"
        : resolvedPosition === "right"
          ? "translate(0, -50%)"
          : "translate(-50%, -100%)";

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex items-center ${className}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={visible ? id : undefined}
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            className="fixed z-[9999] px-3 py-2 text-xs leading-relaxed text-white bg-slate-800 rounded-lg shadow-xl pointer-events-none whitespace-normal break-words"
            style={{ top: coords.top, left: coords.left, maxWidth, transform }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}

// Small info icon with tooltip — use beside labels, headings, table headers
export function HelpTooltip({
  content,
  size = 14,
  className = "",
  position = "top",
  maxWidth = 280,
}) {
  if (!content) return null;
  return (
    <Tooltip content={content} position={position} maxWidth={maxWidth} className={className}>
      <button
        type="button"
        tabIndex={0}
        className="inline-flex text-slate-400 hover:text-indigo-500 focus:text-indigo-500 focus:outline-none transition-colors"
        aria-label="More information"
        onClick={(e) => e.stopPropagation()}
      >
        <HelpCircle size={size} />
      </button>
    </Tooltip>
  );
}

// Inline label with optional help tooltip
export function LabelWithTooltip({ label, tooltip, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <span>{label}</span>
      {tooltip && <HelpTooltip content={tooltip} size={13} />}
    </span>
  );
}

// Badge
export function Badge({ children, variant = "default", size = "sm", title }) {
  const variants = {
    default: "bg-slate-100 text-slate-600",
    primary: "bg-primary-100 text-primary-700",
    success: "bg-emerald-50 text-emerald-700",
    warning: "bg-amber-50 text-amber-700",
    danger: "bg-red-50 text-red-700",
    inactive: "bg-slate-100 text-slate-500",
  };
  const sizes = {
    xs: "text-xs px-2 py-0.5",
    sm: "text-xs px-2.5 py-1",
    md: "text-sm px-3 py-1",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center font-medium rounded-full ${variants[variant]} ${sizes[size]}`}
    >
      {children}
    </span>
  );
}

// Button
export function Button({
  children,
  variant = "primary",
  size = "md",
  className = "",
  disabled,
  loading,
  leftIcon,
  rightIcon,
  ...props
}) {
  const base =
    "inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary:
      "bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 focus:ring-primary-500 shadow-sm",
    secondary:
      "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 active:bg-slate-100 focus:ring-slate-300 shadow-sm",
    ghost:
      "text-slate-600 hover:bg-slate-100 active:bg-slate-200 focus:ring-slate-300",
    danger:
      "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-sm",
    "outline-primary":
      "border border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500",
  };
  const sizes = {
    xs: "text-xs px-2.5 py-1.5",
    sm: "text-sm px-3.5 py-2",
    md: "text-sm px-4 py-2.5",
    lg: "text-base px-5 py-3",
  };
  return (
    <button
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        leftIcon
      )}
      {children}
      {rightIcon}
    </button>
  );
}

// Input
export function Input({
  label,
  error,
  hint,
  leftElement,
  rightElement,
  className = "",
  ...props
}) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-sm font-medium text-slate-700">
          {label}
        </label>
      )}
      <div className="relative">
        {leftElement && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            {leftElement}
          </div>
        )}
        <input
          className={`w-full rounded-lg border bg-white text-sm text-slate-900 placeholder-slate-400 transition-all
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
            ${error ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-slate-200"}
            ${leftElement ? "pl-9" : "pl-3.5"} ${rightElement ? "pr-9" : "pr-3.5"} py-2.5
            ${className}`}
          {...props}
        />
        {rightElement && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
            {rightElement}
          </div>
        )}
      </div>
      {error && (
        <p className="text-xs text-red-700 flex items-start gap-1.5 px-2.5 py-2 rounded-lg bg-red-50 border border-red-200 font-medium">
          <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
          <span className="break-words">{error}</span>
        </p>
      )}
      {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function readSelectOptions(children) {
  return React.Children.toArray(children).flatMap((child, index) => {
    if (!React.isValidElement(child)) return [];
    if (child.type === React.Fragment) return readSelectOptions(child.props.children);
    const label = child.props.children;
    const raw = child.props.value;
    const value = raw !== undefined && raw !== null ? raw : (typeof label === "string" ? label : "");
    return [{ value, label, disabled: !!child.props.disabled, key: `${index}:${value}` }];
  });
}

// Select — custom menu so long country lists stay inside the app window
export function Select({ label, tooltip, error, children, className = "", disabled, value, onChange }) {
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});

  const options = readSelectOptions(children);
  const selected = options.find((item) => String(item.value) === String(value));

  const placeMenu = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
    const height = Math.min(320, Math.max(160, openUp ? spaceAbove : spaceBelow));
    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: Math.max(rect.width, 180),
      maxHeight: height,
      zIndex: 10000,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    placeMenu();
    const close = (event) => {
      if (
        wrapRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) return;
      setOpen(false);
    };
    const onReposition = () => placeMenu();
    document.addEventListener("mousedown", close);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, placeMenu]);

  const pick = (next) => {
    setOpen(false);
    onChange?.({ target: { value: next } });
  };

  return (
    <div className="space-y-1.5">
      {label && (
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium text-slate-700">
            {label}
          </label>
          {tooltip ? <HelpTooltip content={tooltip} size={13} position="bottom" /> : null}
        </div>
      )}
      <div ref={wrapRef} className="relative">
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!disabled) setOpen((cur) => !cur);
          }}
          className={`flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-left text-sm text-slate-900
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all
          disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400
          ${error ? "border-red-300" : ""} ${className}`}
        >
          <span className="min-w-0 truncate">{selected?.label || "Select"}</span>
          <span className="ml-2 shrink-0 text-slate-400">▾</span>
        </button>
        {open && createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            style={menuStyle}
            onMouseDown={(event) => event.stopPropagation()}
            className="overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          >
            {options.map((item) => {
              const on = String(item.value) === String(value);
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={on}
                    disabled={item.disabled}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      pick(item.value);
                    }}
                    className={`flex w-full px-3.5 py-2 text-left text-sm ${
                      on ? "bg-blue-600 text-white" : "text-slate-900 hover:bg-slate-50"
                    } disabled:cursor-not-allowed disabled:text-slate-400`}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
      </div>
      {error && (
        <p className="text-xs text-red-700 flex items-start gap-1.5 px-2.5 py-2 rounded-lg bg-red-50 border border-red-200 font-medium">
          <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
          <span className="break-words">{error}</span>
        </p>
      )}
    </div>
  );
}

// Card
export function Card({ children, className = "", padding = true }) {
  return (
    <div
      className={`bg-white rounded-xl border border-slate-100 shadow-card ${padding ? "p-6" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

// Stat Card (legacy vertical layout)
export function StatCard({
  label,
  value,
  sub,
  icon,
  color = "primary",
  trend,
}) {
  const colors = {
    primary: "bg-primary-50 text-primary-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
    red: "bg-red-50 text-red-600",
  };
  return (
    <Card className="animate-fade-in">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p
            className="mt-2 text-3xl font-bold text-slate-900"
            style={{ fontFamily: "Sora,sans-serif" }}
          >
            {value}
          </p>
          {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
          {trend && (
            <p
              className={`mt-1 text-xs font-medium ${trend.up ? "text-emerald-600" : "text-red-500"}`}
            >
              {trend.label}
            </p>
          )}
        </div>
        {icon && (
          <div className={`p-3 rounded-xl ${colors[color]}`}>{icon}</div>
        )}
      </div>
    </Card>
  );
}

const DASHBOARD_STAT_GRADIENTS = {
  primary: "from-indigo-500 to-violet-600",
  emerald: "from-emerald-400 to-teal-600",
  amber: "from-amber-400 to-orange-500",
  violet: "from-violet-500 to-purple-600",
  red: "from-red-400 to-red-600",
};

const ACCENT_GRADIENTS = {
  "#7dd3fc": "from-sky-400 to-blue-600",
  "#a5b4fc": "from-indigo-400 to-violet-600",
  "#6ee7b7": "from-emerald-400 to-teal-600",
  "#34d399": "from-emerald-500 to-green-600",
  "#fca5a5": "from-red-400 to-red-600",
  "#fcd34d": "from-amber-400 to-orange-500",
  "#94a3b8": "from-slate-400 to-slate-600",
};

function resolveDashboardStatGradient({ iconClass, gradient, color, accent }) {
  if (iconClass) return iconClass;
  if (gradient) return gradient.replace(/^bg-gradient-to-br\s+/, "");
  if (accent && ACCENT_GRADIENTS[accent]) return ACCENT_GRADIENTS[accent];
  return DASHBOARD_STAT_GRADIENTS[color] || DASHBOARD_STAT_GRADIENTS.primary;
}

function renderDashboardStatIcon(icon, loading) {
  if (loading) {
    return <div className="w-5 h-5 rounded bg-white/40 animate-pulse" />;
  }
  if (!icon) return null;
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, {
      size: icon.props.size ?? 18,
      className: `text-white ${icon.props.className || ""}`.trim(),
    });
  }
  if (
    typeof icon !== "function" &&
    !(typeof icon === "object" && icon !== null && icon.$$typeof)
  ) {
    return null;
  }
  // Lucide icons are forwardRef objects ({ $$typeof, render }), not plain functions
  return React.createElement(icon, { size: 18, className: "text-white" });
}

// Admin dashboard stat card — icon and content aligned on the same row
export function DashboardStatCard({
  label,
  title,
  value,
  sub,
  subtitle,
  tooltip,
  icon,
  iconClass,
  gradient,
  color = "primary",
  accent,
  loading = false,
  subIcon,
  onClick,
  active = false,
  allowWrap = false,
}) {
  const displayLabel = label || title;
  const displaySub = sub || subtitle;
  const valueText =
    loading || value == null || value === "" ? (loading ? "…" : value) : value;
  const valueIsLong =
    typeof valueText === "string" && valueText.trim().length > 22;
  const wrapContent = allowWrap || valueIsLong;
  const grad = resolveDashboardStatGradient({
    iconClass,
    gradient,
    color,
    accent,
  });

  return (
    <div
      onClick={onClick}
      className={`@container bg-white rounded-xl border px-3 py-3 flex items-start gap-3 transition-all h-auto ${
        wrapContent ? "min-h-[68px]" : "min-h-[72px]"
      } ${
        active
          ? "border-indigo-600 ring-2 ring-indigo-600/15 shadow-md bg-indigo-50/5"
          : "border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md hover:bg-slate-50/40"
      } ${onClick ? "cursor-pointer" : ""}`}
    >
      <div
        className={`w-10 h-10 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center flex-shrink-0 shadow-md mt-0.5`}
      >
        {renderDashboardStatIcon(icon, loading)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 min-w-0">
          <p
            className={`flex-1 min-w-0 text-[clamp(8px,9cqi,10px)] text-slate-600 uppercase tracking-wide font-semibold ${
              wrapContent
                ? "leading-snug break-words"
                : "overflow-x-auto whitespace-nowrap no-scrollbar"
            }`}
            title={typeof displayLabel === "string" ? displayLabel : undefined}
          >
            {displayLabel}
          </p>
          {tooltip ? (
            <HelpTooltip content={tooltip} size={13} className="flex-shrink-0" />
          ) : null}
        </div>
        <p
          className={`mt-0.5 font-bold text-slate-900 ${
            wrapContent
              ? "text-sm leading-relaxed break-words [overflow-wrap:anywhere] whitespace-normal"
              : "text-lg leading-tight truncate"
          }`}
          style={{ fontFamily: "Sora, sans-serif" }}
        >
          {valueText}
        </p>
        {displaySub && (
          <p
            className={`mt-0.5 text-[10px] text-slate-500 font-medium min-w-0 ${
              wrapContent
                ? "leading-relaxed break-words [overflow-wrap:anywhere]"
                : "truncate"
            }`}
          >
            {subIcon}
            {displaySub}
          </p>
        )}
      </div>
    </div>
  );
}

// Layering — toasts must sit above modal backdrops
export const MODAL_Z_INDEX = 200;
export const TOAST_Z_INDEX = 10000;

// Full-screen backdrop overlay (mobile nav, drawers, etc.)
export function Overlay({
  open,
  onClose,
  zIndex = 100,
  className = "bg-slate-900/40 backdrop-blur-md",
  children,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className={`fixed inset-0 w-screen h-screen min-h-[100dvh] ${className}`}
      style={{ zIndex }}
      onClick={onClose}
      role="presentation"
    >
      {children}
    </div>,
    document.body,
  );
}

// Modal — portals to document.body for full-viewport blur coverage
export function Modal({
  open,
  onClose,
  title,
  header,
  children,
  size = "md",
  zIndex = MODAL_Z_INDEX,
  align = "center",
  noPadding = false,
  bare = false,
  panelClassName = "",
}) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const sizes = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    detail: "max-w-[860px]",
  };
  const alignClass =
    align === "top"
      ? "items-start justify-center py-6 sm:py-8"
      : "items-center justify-center";

  return createPortal(
    <>
      <div
        className="fixed inset-0 w-screen h-screen min-h-[100dvh] bg-slate-900/45 backdrop-blur-md"
        style={{ zIndex }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`fixed inset-0 overflow-y-auto flex ${alignClass} px-4 sm:px-6`}
        style={{ zIndex: zIndex + 1 }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
      >
        {bare ? (
          <div
            className="w-full flex justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        ) : (
          <div
            className={`relative w-full ${sizes[size] || sizes.md} max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden bg-white rounded-2xl shadow-2xl animate-slide-up ${panelClassName}`}
            onClick={(e) => e.stopPropagation()}
          >
            {(header || title) && (
              <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100 flex-shrink-0">
                {header || (
                  <h3 className="text-lg font-semibold text-slate-900">
                    {title}
                  </h3>
                )}
                {!header && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            )}
            <div
              className={`${noPadding ? "" : "p-6"} overflow-y-auto flex-1 min-h-0`}
            >
              {children}
            </div>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}

export function FormErrorsSummary({ errors, title = "Please fix the following:" }) {
  const messages = Object.entries(errors || {})
    .filter(([, message]) => Boolean(message))
    .map(([field, message]) => ({ field, message }));
  if (!messages.length) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-800 shadow-sm"
    >
      <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        <ul className="mt-1 space-y-0.5">
          {messages.map(({ field, message }) => (
            <li key={field} className="text-xs sm:text-sm break-words">
              {message}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function Toast({ notification }) {
  if (!notification) return null;
  const config = {
    success: {
      icon: <CheckCircle size={16} />,
      bg: "bg-emerald-50",
      text: "text-emerald-800",
      border: "border-emerald-200",
    },
    error: {
      icon: <AlertCircle size={16} />,
      bg: "bg-red-50",
      text: "text-red-800",
      border: "border-red-200",
    },
    info: {
      icon: <Info size={16} />,
      bg: "bg-primary-50",
      text: "text-primary-800",
      border: "border-primary-200",
    },
  };
  const c = config[notification.type] || config.success;
  return createPortal(
    <div
      className={`fixed top-5 right-5 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg animate-slide-up max-w-md ${c.bg} ${c.text} ${c.border}`}
      style={{ zIndex: TOAST_Z_INDEX }}
      role="status"
    >
      {c.icon}
      <span className="text-sm font-medium">{notification.message}</span>
    </div>,
    document.body,
  );
}

// Empty State
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="text-slate-300 mb-4">{icon}</div>
      <h3 className="text-base font-semibold text-slate-700 mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-slate-500 mb-6 max-w-sm">{description}</p>
      )}
      {action}
    </div>
  );
}

// Module Locked Overlay — shown over cards/panels for modules not in the
// tenant's current license plan. Designed to look like a premium "upgrade"
// paywall (à la Notion/Linear/Slack plan-gating) rather than a plain error
// state — the card/data underneath stays visible-but-obscured, never removed.
//
// Pass `onUpgrade` (e.g. a navigate-to-billing callback) for roles that can
// actually act on it — a CTA button renders. Omit it for roles that can't
// (e.g. end users) and a neutral "ask your admin" pill renders instead.
export function ModuleLockedOverlay({
  moduleLabel = "This module",
  onUpgrade,
  ctaLabel = "Upgrade License",
}) {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2xl bg-white/92 backdrop-blur-[3px] text-center px-6 py-5">
      <div className="relative">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-indigo-200/70">
          <Crown size={18} className="text-white" strokeWidth={2.25} />
        </div>
        {/* <span className="absolute -top-1.5 -right-2 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[9px] font-bold px-1.5 py-[2px] rounded-full shadow-sm tracking-wide">
          PRO
        </span> */}
      </div>

      <div className="space-y-1">
        <p className="text-sm font-semibold text-slate-800">
          {moduleLabel} is a premium module
        </p>
        <p className="text-xs text-slate-500 leading-snug max-w-[230px] mx-auto">
          Upgrade your organization's license to unlock {moduleLabel} and give
          your team full access.
        </p>
      </div>

      {onUpgrade ? (
        <button
          type="button"
          onClick={onUpgrade}
          className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-md shadow-indigo-200/80 transition-all"
        >
          {ctaLabel}
          <ArrowUpRight
            size={13}
            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </button>
      ) : (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium text-slate-500 bg-slate-100 border border-slate-200">
          Ask support to upgrade
        </span>
      )}
    </div>
  );
}

// Risk Score Ring
export function RiskScoreRing({ score, size = 80 }) {
  if (score === null || score === undefined)
    return <div className="text-slate-400 text-sm">Pending</div>;
  const color = score < 35 ? "#10b981" : score < 65 ? "#f59e0b" : "#ef4444";
  const label = score < 35 ? "Low" : score < 65 ? "Medium" : "High";
  const r = size / 2 - 8;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="6"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dasharray 0.8s ease" }}
        />
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          fill="#1e293b"
          fontSize="14"
          fontWeight="700"
          fontFamily="Sora"
        >
          {score}
        </text>
      </svg>
      <span className="text-xs font-semibold" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

// Progress Bar
export function ProgressBar({ value, max, color = "primary", size = "sm" }) {
  const numeric = Number(value) || 0;
  const pct = max != null && max > 0
    ? Math.min((numeric / max) * 100, 100)
    : Math.min(numeric, 100);
  const colors = {
    primary: "bg-primary-500",
    indigo: "bg-indigo-500",
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
  };
  const heights = { sm: "h-1.5", md: "h-3", lg: "h-4" };
  const h = heights[size] || heights.sm;
  return (
    <div className={`w-full bg-slate-200/80 rounded-full ${h}`} title={`${Math.round(pct)}% complete`}>
      <div
        className={`${h} rounded-full transition-all duration-500 ${colors[color] || colors.primary}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// Table
export function Table({ columns, data, emptyMessage = "No data found" }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-100">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-100">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider ${col.className || ""}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-10 text-center text-slate-400 text-sm"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, i) => (
              <tr
                key={row.id || i}
                className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors"
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-3.5 text-slate-700 ${col.className || ""}`}
                  >
                    {col.render
                      ? col.render(row[col.key], row, i)
                      : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination({
  page = 1,
  pages = 1,
  total = 0,
  pageSize = 20,
  onPageChange,
  itemLabel = "items",
  className = "",
}) {
  if (!total) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  // Generate page numbers with ellipsis truncation for cleaner UI
  const getPageNumbers = () => {
    const pageNumbers = [];
    if (pages <= 5) {
      for (let i = 1; i <= pages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, page - 1);
      let endPage = Math.min(pages, page + 1);

      if (page <= 2) {
        startPage = 1;
        endPage = 3;
      } else if (page >= pages - 1) {
        startPage = pages - 2;
        endPage = pages;
      }

      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) {
          pageNumbers.push("...");
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < pages) {
        if (endPage < pages - 1) {
          pageNumbers.push("...");
        }
        pageNumbers.push(pages);
      }
    }
    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div
      className={`flex flex-col sm:flex-row items-center justify-between gap-4 w-full pt-3 ${className}`}
    >
      <p className="text-xs font-semibold text-slate-500">
        Showing <span className="font-bold text-slate-800">{start}</span> to{" "}
        <span className="font-bold text-slate-800">{end}</span> of{" "}
        <span className="font-bold text-slate-800">{total}</span> {itemLabel}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange?.(page - 1)}
          disabled={page <= 1}
          className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs font-bold transition-all hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        <div className="flex items-center gap-1">
          {pageNumbers.map((p, idx) =>
            p === "..." ? (
              <span
                key={`ellipsis-${idx}`}
                className="px-2 text-xs font-bold text-slate-400"
              >
                ...
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange?.(p)}
                className={`w-8 h-8 rounded-xl text-xs font-bold transition-all ${
                  page === p
                    ? "bg-blue-600 text-white shadow-md font-extrabold"
                    : "border border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
              >
                {p}
              </button>
            ),
          )}
        </div>

        <button
          type="button"
          onClick={() => onPageChange?.(page + 1)}
          disabled={page >= pages}
          className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs font-bold transition-all hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}

/** Preview row for uploaded assessment attachments (Gap, TPRM, etc.). */
export function AttachmentPreviewRow({
  file,
  previewPath,
  className = "",
  onRemove,
  removing = false,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const name = file?.original_name || file?.file_name || "Attachment";
  const sizeLabel = formatAttachmentSize(file?.file_size_bytes);

  const handlePreview = async () => {
    if (!previewPath || loading) return;
    setError("");
    setLoading(true);
    try {
      await openAttachmentPreview(previewPath);
    } catch (err) {
      setError(err.message || "Could not open attachment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-2 rounded-lg border border-indigo-500/15 bg-white px-2.5 py-2">
        <FileText size={14} className="text-indigo-500 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-slate-800 truncate m-0" title={name}>
            {name}
          </p>
          {sizeLabel && (
            <p className="text-[10px] text-slate-400 m-0">{sizeLabel}</p>
          )}
        </div>
        <button
          type="button"
          onClick={handlePreview}
          disabled={!previewPath || loading}
          className="inline-flex items-center gap-1 shrink-0 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
          title="Open preview in new tab"
        >
          {loading ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Eye size={12} />
          )}
          Preview
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(file)}
            disabled={removing}
            aria-label="Remove attachment"
            title="Remove attachment"
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-60 transition-colors"
          >
            {removing ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <X size={14} strokeWidth={2.25} />
            )}
          </button>
        )}
      </div>
      {error && (
        <p className="text-[10px] text-red-600 mt-1 mb-0">{error}</p>
      )}
    </div>
  );
}
